<template>
  <article class="change-card">
    <header class="change-card-head">
      <button type="button" class="change-breadcrumb" title="Open document" @click="openDocument">
        <template v-for="(segment, index) in pathSegments" :key="index">
          <ChevronRight v-if="index > 0" class="icon change-breadcrumb-sep" />
          <span :class="index === pathSegments.length - 1 ? 'change-breadcrumb-name' : 'change-breadcrumb-seg'">{{ segment }}</span>
        </template>
      </button>
      <div class="change-stats">
        <template v-if="summary.aggregateDiffAvailable">
          <span class="diff-stat diff-stat-added">+{{ Number(summary.addedLineCount) }}</span>
          <span class="diff-stat diff-stat-removed">−{{ Number(summary.deletedLineCount) }}</span>
        </template>
        <span v-else class="diff-stat diff-stat-muted">
          <EyeOff class="icon" />
          <span>diff unavailable</span>
        </span>
      </div>
    </header>

    <div v-if="wasRenamed" class="history-rename change-card-rename">
      <Pencil class="icon" />
      <span>Renamed <s>{{ summary.previousTitle }}</s> → {{ summary.title }}</span>
    </div>

    <div class="change-card-foot">
      <div class="editor-row">
        <div class="editor-avatars">
          <UserAvatar
            v-for="(editor, index) in summary.editors"
            :key="index"
            size="editor"
            class="editor-avatar"
            :title="editorName(editor)"
            :name="editor.userName ?? editor.userEmail"
            aria-hidden="true"
          />
        </div>
        <span class="editor-summary">{{ editorSummary }}</span>
      </div>
      <button
        v-if="summary.aggregateDiffAvailable"
        type="button"
        class="btn diff-toggle"
        @click="toggleDiff"
      >
        <component :is="expanded ? ChevronUp : ChevronDown" class="icon" />
        <span>Diff</span>
      </button>
    </div>

    <div v-if="diffError" class="change-card-error">{{ diffError }}</div>
    <div v-if="expanded && diff" class="change-card-diff">
      <UnifiedDiff :hunks="diff.diffHunks" :diff-available="diff.diffAvailable" />
    </div>
    <div v-else-if="expanded && diffLoading" class="change-card-loading">Loading diff…</div>
  </article>
</template>

<script setup lang="ts">
import { ChevronDown, ChevronRight, ChevronUp, EyeOff, Pencil } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  QueriesService,
  type DocumentChangeFeedDocumentResponse,
  type DocumentChangeFeedEditorResponse,
  type GetDocumentChangeFeedDocumentDiffQueryResponse,
  type Instant
} from '../api/generated';
import { formatInstantTime } from '../format';
import { getErrorMessage } from '../stores/session';
import UnifiedDiff from './UnifiedDiff.vue';
import UserAvatar from './UserAvatar.vue';

const props = defineProps<{
  summary: DocumentChangeFeedDocumentResponse;
  windowStartAtUtc: Instant;
  windowEndAtUtc: Instant;
}>();

const router = useRouter();

const pathSegments = computed(() => props.summary.documentPath.split('/').filter(Boolean));

const wasRenamed = computed(() =>
  props.summary.previousTitle != null
  && props.summary.previousTitle.length > 0
  && props.summary.previousTitle !== props.summary.title);

const expanded = ref(false);
const diff = ref<GetDocumentChangeFeedDocumentDiffQueryResponse | null>(null);
const diffLoading = ref(false);
const diffError = ref<string | null>(null);

const editorSummary = computed(() => {
  const editorCount = props.summary.editors.length;
  const editorLabel = `${editorCount} editor${editorCount === 1 ? '' : 's'}`;
  const editCount = Number(props.summary.historyEntryCount);
  const editLabel = `${editCount} edit${editCount === 1 ? '' : 's'}`;
  return `${editorLabel} · ${editLabel} · last ${formatInstantTime(props.summary.lastEditedAt)}`;
});

function editorName(editor: DocumentChangeFeedEditorResponse): string {
  return editor.userName ?? editor.userEmail ?? 'Unknown editor';
}

async function toggleDiff(): Promise<void> {
  expanded.value = !expanded.value;
  if (!expanded.value || diff.value != null || diffLoading.value) {
    return;
  }

  diffLoading.value = true;
  diffError.value = null;
  try {
    diff.value = await QueriesService.queryGetDocumentChangeFeedDocumentDiffQuery({
      requestBody: {
        documentId: props.summary.documentId,
        windowStartAtUtc: props.windowStartAtUtc,
        windowEndAtUtc: props.windowEndAtUtc
      }
    });
  } catch (error) {
    diffError.value = getErrorMessage(error, 'Unable to load diff.');
  } finally {
    diffLoading.value = false;
  }
}

async function openDocument(): Promise<void> {
  await router.push({ name: 'workspace-document', params: { documentId: String(props.summary.documentId) } });
}
</script>
